import { ClinicianLabelHistoryEntry, ClinicianLabels, TestCommentEntry } from "types/tests";
import { Table, Text } from "@mantine/core";

import { clinicianLabelFieldKey } from "@constants/clinicianLabelConstants";
import { formatClinicianLabel } from "@utils/clinicianLabelUtils";
import { formatDateAndTime } from "@utils/CommonUtil";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface ClinicianReviewHistoryProps {
  labels?: ClinicianLabels;
  commentHistory?: TestCommentEntry[];
}

interface TimelineRow {
  at: string;
  user: string;
  what: string;
  detail: string;
}

export default function ClinicianReviewHistory({
  labels,
  commentHistory,
}: ClinicianReviewHistoryProps) {
  const { t } = useTranslation();

  const rows = useMemo<TimelineRow[]>(() => {
    const labelRows = (labels?.history ?? []).map((entry: ClinicianLabelHistoryEntry) => ({
      at: entry.changedAt,
      user: entry.userName ?? entry.userId,
      what: t(clinicianLabelFieldKey(entry.field)),
      detail: formatClinicianLabel(entry, t),
    }));

    const commentRows = (commentHistory ?? []).map((entry) => ({
      at: entry.createdAt,
      user: entry.userName ?? entry.userId,
      what: t("ClinicianLabel.historyComment"),
      detail: entry.text,
    }));

    return [...labelRows, ...commentRows].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
  }, [labels?.history, commentHistory, t]);

  if (rows.length === 0) {
    return <Text c="dimmed">{t("ClinicianLabel.historyEmpty")}</Text>;
  }

  return (
    <Table data-testid="clinician-review-history">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("ResultList.date")}</Table.Th>
          <Table.Th>{t("ClinicianLabel.historyUser")}</Table.Th>
          <Table.Th>{t("ClinicianLabel.historyChange")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={`${row.at}-${row.what}-${index}`}>
            <Table.Td>{formatDateAndTime(row.at)}</Table.Td>
            <Table.Td>{row.user}</Table.Td>
            <Table.Td>
              <Text span fw={600} size="sm">
                {row.what}:
              </Text>{" "}
              {row.detail}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
